import React, { useState } from 'react';
import './PhotoSubmissionModal.css';

// Image compression function
const compressImage = (file, maxWidth = 1920, quality = 0.8) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img;
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(resolve, 'image/jpeg', quality);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

const PhotoSubmissionModal = ({ isOpen, onClose }) => {
  const [selectedTheme, setSelectedTheme] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [description, setDescription] = useState('');
  const [submitterName, setSubmitterName] = useState('');
  const [submitterEmail, setSubmitterEmail] = useState('');
  const [agreedToRules, setAgreedToRules] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showGuidelines, setShowGuidelines] = useState(false);

  const themes = {
    pets: {
      name: "🐾 Pet Pals",
      description: "Cute cats, dogs, and other furry friends",
      color: "#ff69b4"
    },
    scenery: {
      name: "🏞️ Scenic Views", 
      description: "Beautiful landscapes, sunsets, and nature",
      color: "#00ff00"
    },
    cartoons: {
      name: "🎨 Artistic Cartoons",
      description: "Any themed cartoons, drawings, AI art, or artistic photos",
      color: "#8a2be2"
    }
  };

  const submissionRules = {
    general: [
      "No copyrighted content or trademarks",
      "Photos must be original - taken by you or you have permission to use",
      "No downloaded images from the internet",
      "Appropriate content only",
      "Minimum resolution: 400x400 pixels",
      "Maximum file size: 8MB"
    ],
    themeSpecific: {
      pets: [
        "Only pets, no humans visible",
        "Clear, well-lit photos",
        "No animal cruelty or inappropriate content",
        "Focus on cute, friendly animals"
      ],
      scenery: [
        "Original landscape photos only - photos you took yourself",
        "No famous landmarks or copyrighted locations",
        "Natural beauty preferred",
        "Avoid city skylines with buildings"
      ],
      cartoons: [
        "Original artwork, drawings, or AI-generated art",
        "Any theme: pets, coding, scenery, crafts, etc.",
        "No offensive or inappropriate content",
        "Creative and artistic content encouraged"
      ]
    }
  };

  const showErrorPopup = (message) => {
    setErrorMessage(message);
    setShowError(true);
  };

  // Function to validate and auto-correct email addresses
  const validateAndCorrectEmail = (email) => {
    if (!email || !email.trim()) {
      return { isValid: false, correctedEmail: '', error: 'Email is required' };
    }
    
    let correctedEmail = email.trim().toLowerCase();
    
    // Auto-correct common Gmail issues
    if (correctedEmail.endsWith('@gmail')) {
      correctedEmail = correctedEmail + '.com';
    }
    
    // Basic email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!emailRegex.test(correctedEmail)) {
      return { isValid: false, correctedEmail: correctedEmail, error: 'Please enter a valid email address' };
    }
    
    return { isValid: true, correctedEmail: correctedEmail, error: '' };
  };

  const validateForm = () => {
    if (!selectedTheme) {
      showErrorPopup('Please select a theme for your photo submission. Click on one of the theme options above.');
      return false;
    }
    
    if (!selectedFile) {
      showErrorPopup('Please select a photo to upload. Click the upload area to choose your image file.');
      return false;
    }
    
    if (!submitterName.trim()) {
      showErrorPopup('Please enter your name in the "Your Information" section.');
      return false;
    }
    
    // Validate and auto-correct email
    const emailValidation = validateAndCorrectEmail(submitterEmail);
    if (!emailValidation.isValid) {
      showErrorPopup(`Email validation failed: ${emailValidation.error}`);
      return false;
    }
    
    // Update the email field with the corrected version if it was auto-corrected
    if (emailValidation.correctedEmail !== submitterEmail) {
      setSubmitterEmail(emailValidation.correctedEmail);
    }
    
    if (!description.trim()) {
      showErrorPopup('Please provide a description of your photo in the "Photo Details" section.');
      return false;
    }
    
    if (!agreedToRules) {
      showErrorPopup('Please check the box to agree to the submission rules and guidelines.');
      return false;
    }
    
    return true;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      showErrorPopup('Please select an image file (JPG, PNG, GIF). The file you selected is not a valid image format.');
      return;
    }

    // Check file size (8MB limit)
    if (file.size > 8 * 1024 * 1024) {
      showErrorPopup('The image file is too large. Please select an image that is 8MB or smaller.');
      return;
    }

    try {
      // Compress the image
      const compressedBlob = await compressImage(file);
      
      // Create a new file from the compressed blob
      const compressedFile = new File([compressedBlob], file.name, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });
      
      setSelectedFile(compressedFile);
      
      // Show preview
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target.result);
      reader.readAsDataURL(compressedFile);
      
      // Log compression results
      const originalSize = (file.size / 1024 / 1024).toFixed(2);
      const compressedSize = (compressedFile.size / 1024 / 1024).toFixed(2);
      console.log(`Image compressed: ${originalSize}MB → ${compressedSize}MB`);
      
    } catch (error) {
      console.error('Error compressing image:', error);
      // Fallback to original file if compression fails
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append('photo', selectedFile);
      formData.append('theme', selectedTheme);
      formData.append('description', description);
      formData.append('submitterName', submitterName);
      formData.append('submitterEmail', submitterEmail);
      
      const response = await fetch('/api/photo-submissions', {
          method: 'POST',
          body: formData
      });
      
      const responseData = await response.json();
      
      if (response.ok && responseData.success) {
          setShowSuccess(true);
      } else {
          // Show the specific error message from the server
          const errorMessage = responseData.message || responseData.error || 'Submission failed';
          showErrorPopup(`Submission failed: ${errorMessage}`);
      }
    } catch (error) {
        console.error('Submission failed:', error);
        showErrorPopup('Submission failed. Please check your internet connection and try again.');
    } finally {
        setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedTheme('');
    setSelectedFile(null);
    setDescription('');
    setSubmitterName('');
    setSubmitterEmail('');
    setAgreedToRules(false);
    setPreviewUrl('');
    setShowError(false);
    setShowSuccess(false);
    setShowGuidelines(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="photo-submission-overlay">
      <div className="photo-submission-modal">
        <button className="close-btn" onClick={handleClose}>×</button>
        <div className="modal-header">
          <h2>📸 Submit Your Photos</h2>
          <p className="contest-description">
            Share your best photos with our community! 
            <br />
            <span className="highlight">Selected photos become new game themes with full attribution to you!</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="submission-form">
          <div className="form-section">
            <div className="section-header">
              <h3>📸 Choose Your Theme *</h3>
              <button 
                type="button"
                className="guidelines-btn"
                onClick={() => setShowGuidelines(true)}
                title="View Photo Guidelines"
              >
                📋
              </button>
            </div>
            <div className="theme-dropdown-container">
              <select
                value={selectedTheme}
                onChange={(e) => setSelectedTheme(e.target.value)}
                className="theme-dropdown"
                required
              >
                <option value="">Select a theme...</option>
                {Object.entries(themes).map(([key, theme]) => (
                  <option key={key} value={key}>
                    {theme.name} - {theme.description}
                  </option>
                ))}
              </select>
            </div>
            {selectedTheme && (
              <div className="selected-theme-display">
                <div className="theme-option selected">
                  <div className="theme-icon">{themes[selectedTheme].name.split(' ')[0]}</div>
                  <div className="theme-info">
                    <h4>{themes[selectedTheme].name.split(' ').slice(1).join(' ')}</h4>
                    <p>{themes[selectedTheme].description}</p>
                  </div>
                  <div className="selected-indicator">✓</div>
                </div>
              </div>
            )}
          </div>

          {selectedTheme && (
            <div className="form-section">
              <h3>📋 Submission Guidelines</h3>
              <div className="guidelines-box">
                <h4>General Rules:</h4>
                <ul>
                  {submissionRules.general.map((rule, index) => (
                    <li key={index}>{rule}</li>
                  ))}
                </ul>
                
                <h4>Theme-Specific Rules:</h4>
                <ul>
                  {submissionRules.themeSpecific[selectedTheme]?.map((rule, index) => (
                    <li key={index}>{rule}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div className="form-section">
            <h3>👤 Your Information *</h3>
            <div className="form-group">
              <label htmlFor="submitterName">Your Name *</label>
              <input
                type="text"
                id="submitterName"
                value={submitterName}
                onChange={(e) => setSubmitterName(e.target.value)}
                placeholder="Enter your real name"
                required
              />
              <small>I'll contact you if your photo is selected for the game!</small>
            </div>
            <div className="form-group">
              <label htmlFor="submitterEmail">Your Email *</label>
              <input
                type="email"
                id="submitterEmail"
                value={submitterEmail}
                onChange={(e) => setSubmitterEmail(e.target.value)}
                placeholder="Enter your email address"
                required
              />
              <small>I'll notify you if your photo is selected for the game</small>
            </div>
          </div>

          <div className="form-section">
            <h3>🖼️ Your Photo *</h3>
            <div className="file-upload-area">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                id="photo-upload"
                required
              />
              <label htmlFor="photo-upload" className="file-upload-label">
                {previewUrl ? (
                  <div className="image-preview">
                    <img src={previewUrl} alt="Preview" />
                    <span>Click to change photo</span>
                  </div>
                ) : (
                  <div className="upload-placeholder">
                    <span className="upload-icon">📷</span>
                    <span>Click to select photo</span>
                    <small>Max size: 8MB, Formats: JPG, PNG, GIF</small>
                  </div>
                )}
              </label>
            </div>
          </div>

          <div className="form-section">
            <h3>📝 Photo Details *</h3>
            <div className="form-group">
              <label>Description *</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Tell me about your photo..."
                maxLength={200}
                required
              />
              <small>{description.length}/200 characters</small>
            </div>
          </div>

          <div className="form-section">
            <div className="rules-agreement">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={agreedToRules}
                  onChange={(e) => setAgreedToRules(e.target.checked)}
                  required
                />
                <span className="checkmark"></span>
                I have read and agree to follow all submission rules and guidelines
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-btn" 
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="submit-btn"
              disabled={!agreedToRules || !selectedFile || !submitterName || !selectedTheme || isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : '📸 Submit Photo'}
            </button>
          </div>
        </form>

        {/* Guidelines Modal */}
        {showGuidelines && (
          <div className="guidelines-overlay">
            <div className="guidelines-modal">
              <div className="guidelines-header">
                <h2>📸 Theme 2 - Community Photo Sharing 📸</h2>
                <button className="guidelines-close-btn" onClick={() => setShowGuidelines(false)}>×</button>
              </div>
              <div className="guidelines-content">
                <div className="photo-guidelines">
                  <h3>📸 Photo Submission Guidelines</h3>
                  <div className="guidelines-grid">
                    <div className="guideline-item">
                      <span className="guideline-icon">📏</span>
                      <span><strong>Photo Size:</strong> High quality images, 8MB maximum file size</span>
                    </div>
                    <div className="guideline-item">
                      <span className="guideline-icon">📧</span>
                      <span><strong>Email Format:</strong> Enter your correct email address for notifications</span>
                    </div>
                    <div className="guideline-item">
                      <span className="guideline-icon">👤</span>
                      <span><strong>Your Name:</strong> Provide your name for photo attribution</span>
                    </div>
                    <div className="guideline-item">
                      <span className="guideline-icon">📝</span>
                      <span><strong>Description:</strong> Add a description you want for your photo</span>
                    </div>
                    <div className="guideline-item">
                      <span className="guideline-icon">🎨</span>
                      <span><strong>Theme Rules:</strong> Choose pets, scenery, or cartoons - follow the selected theme</span>
                    </div>
                    <div className="guideline-item">
                      <span className="guideline-icon">✅</span>
                      <span><strong>Original Content:</strong> Only submit photos you took yourself or have permission to use</span>
                    </div>
                    <div className="guideline-item">
                      <span className="guideline-icon">🚫</span>
                      <span><strong>No Inappropriate Content:</strong> No improper images, offensive descriptions, or copyrighted material</span>
                    </div>
                    <div className="guideline-item">
                      <span className="guideline-icon">👥</span>
                      <span><strong>Community Credit:</strong> Selected photos become memory game cards with your attribution</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="guidelines-actions">
                <button 
                  className="guidelines-action-btn"
                  onClick={() => setShowGuidelines(false)}
                >
                  Got It - Back to Submission
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Success Popup */}
        {showSuccess && (
          <div className="success-popup-overlay">
            <div className="success-popup">
              <div className="success-icon">🎉</div>
              <h2>Photo Submitted Successfully!</h2>
              <p>Your photo has been submitted and will be reviewed within 24 hours.</p>
              <p className="success-note">
                You'll receive an email notification if your photo is selected for the game!
              </p>
              <button 
                className="success-close-btn"
                onClick={() => {
                  setShowSuccess(false);
                  handleClose();
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Error Popup */}
        {showError && (
          <div className="error-popup-overlay">
            <div className="error-popup">
              <div className="error-icon">❌</div>
              <h2>Submission Error</h2>
              <p>{errorMessage}</p>
              <button 
                className="error-close-btn"
                onClick={() => setShowError(false)}
              >
                Got It
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoSubmissionModal; 